import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ActionIconDef, Grid as InavistaGrid, GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridRow,
  GridRowClickEvent, GridRowCommitEvent, GridRowEditStartEvent, GridToolbarActionClickEvent,
} from '@inavista/components/grid';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { partsOrderStatuses } from 'projects/service/src/app/modules/home/models/orders.model';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ApiResponse, OrderTypes, UserRoles, PartsAvailabilityStatus } from 'projects/service/src/app/shared/models/app.model';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { CellEdit, DropdownEditor, NumberEditor, TextEditor } from 'projects/service/src/app/shared/utils/cell-editors';
import {
  ADD_ITEM_ACTION, buildItemsActionIcons, buildItemsGridConfig, ITEM_OPEN_FIELD, ITEM_ROW_KEY, itemEditFields, itemFields,
  itemRowKey, itemsSchema, PARTS_ORDER_ITEMS_URL,
} from './items-tab.grid.config';

// The old grid's height: the popup body less 330px of rows area, plus its toolbar and header.
const POPUP_CHROME_PX = 330;
const GRID_TOOLBAR_AND_HEADER_PX = 80;
const NUMBER_FIELDS = new Set(['quantityShippedInInventoryUnit', 'quantityReceived']);

@Component({
    selector: 'app-items-tab',
    templateUrl: './items-tab.component.html',
    styleUrls: ['./items-tab.component.scss'],
    // The grid reads the popup's own items array, not an endpoint.
    providers: [...provideLocalGridRows()],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ItemsTabComponent implements OnInit, OnChanges, OnDestroy {

  USER;
  orderType = OrderTypes.shipmentOrder;
  statuses = partsOrderStatuses;
  showItemsGrid: boolean = false;

  validationApplied: boolean = false;
  skipBeginEditEvent: boolean = false;
  columns: Object[];
  itemsObject: DropdownEditor;

  itemGroup: any;
  itemGroupObject: TextEditor;

  itemDescriptionObject: TextEditor;

  itemQuantityObject: NumberEditor;



  selectedRowId: string;
  showItemAdvancedSearchPopup: boolean = false;

  isReadOnly: boolean = false;

  onItemUpdate = new Subject<string>();

  gridHeight: any;
  @Input() status;
  @Input() isManager: boolean;
  @Input() items = [];
  @Input() technicianId: string;
  itemsList: any;
  isCSA: boolean = false;
  showLoader: boolean = false;
  @Input() inventoryItems: any;
  @Input() reasonsDDL: any;

  @Output() onQuantityReceivedUpdate = new EventEmitter();
  @Output() onQuantityReceivedReset = new EventEmitter();
  @Output() onShipmentIdClick: EventEmitter<any> = new EventEmitter();

  // ---- @inavista/components grid ----------------------------------------------------------------------------------
  @ViewChild('partsOrderItemsGrid') partsOrderItemsGrid: InavistaGrid;
  @ViewChild('itemCodeEditor', { static: true }) itemCodeEditor: TemplateRef<unknown>;
  @ViewChild('itemDescriptionEditor', { static: true }) itemDescriptionEditor: TemplateRef<unknown>;
  @ViewChild('serviceItemGroupEditor', { static: true }) serviceItemGroupEditor: TemplateRef<unknown>;
  @ViewChild('quantityEditor', { static: true }) quantityEditor: TemplateRef<unknown>;

  private readonly gridRows = inject(LocalRowsGridApi);
  gridConfig: GridConfig = null;
  actionIcons: ActionIconDef[] = [];
  /** The array the grid shows (the old grid's dataSource): the items input. */
  gridItems: any[] = [];
  /** A row is open for editing (the old grid's isEdit). */
  editing = false;
  /** Each column's editor (the old column `edit`), drawn in its cell by CellEditorHost. */
  edits: Record<string, CellEdit> = {};
  /** The editors open in the editing row, by field: read on save, as the Syncfusion grid did. */
  readonly openEditors = new Map<string, CellEdit>();
  /** The editors of hidden columns, drawn out of sight while a row is open (the old grid drew them in hidden cells). */
  hiddenEditFields: string[] = [];
  hiddenEditRow: any = null;
  private builtInEditFields: string[] = [];
  private deleteLockReason: string | null = null;

  constructor(
    private commonService: CommonService,
    private dialog: MatDialog,
    private eRef: ElementRef
  ) {
    this.USER = this.commonService.USER;
  }

  showMasterItemCodePopup(eqData: any) {
    if (!eqData?.itemCode) return;
    const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
      panelClass: 'master-itemcode-popup-dialog',
      width: '80%',
      height: '80%',
      data: eqData
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }


  @HostListener('document:click', ['$event'])
  onClick(event) {
    // The click that opened the row: its cell content was replaced by the editors, so its target is gone.
    if (this.editing && (event.target as Node)?.isConnected !== false) {
      if (!this.eRef.nativeElement.contains(event.target)
        && !document.getElementsByClassName('swal2-container')[0]?.contains(event.target)
        // The item search, a dialog or an @inavista list opened over the open row (the old ones were inside eRef).
        && !(event.target as HTMLElement)?.closest?.('app-items-advanced-search, .cdk-overlay-container, [class*="inavista"]')
      ) {
        this.endEdit();
      }
    }
  }

  ngOnInit(): void {
    this.gridHeight = document.getElementById('parts-order-popup-body').clientHeight - POPUP_CHROME_PX + GRID_TOOLBAR_AND_HEADER_PX;
    this.isCSA = this.USER.role === UserRoles.csa;
    this.isReadOnly = this.USER.role === UserRoles.readOnly
    this.columns = this.getGridColumns();
    this.showLoader = false;
    this.setItemsData();
    this.getItemList();
    this.onItemUpdate
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      ).subscribe(value => {
        if (this.itemsObject) {
          this.itemsObject.showPopup();
          this.itemsObject.showSpinner();
        }
        this.getItemList(value);
      });
    this.setupGrid();
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes.items) {
      // The old grid was drawn again on the new items (onGridCreated: dataSource = this.items).
      this.gridItems = this.items;
    }
    this.refreshRows();
  }

  setItemsData() {
    this.items?.map((item, index) => {
      return this.setItemsRow(item, index);
    });
    if (this.status === partsOrderStatuses.open) {
      this.addEmptyRowsToItemsData();
    }
  }

  addEmptyRowsToItemsData() {
    for (let i = 0; i < 2; i++) {
      this.items.push(this.setItemsRow({}, this.items.length));
    }
  }

  setItemsRow(item, index) {
    item.id = item.id || 0,
      item.shipmentOrderId = item.shipmentOrderId || null,
      item.shipmentOrderLN = item.shipmentOrderLN || null,
      item.lineNumber = index + 1;
    item.itemCode = item.itemCode || item.item || '';
    item.itemDescription = item.itemDescription || item.description || '';
    item.itemGroup = item.itemGroup || '';
    item.serviceItemGroup = item.serviceItemGroup || '';
    item.quantity = item.quantity === 0 && item.sequenceLN != 1 ? null : item.quantity || 1;
    item.quantityReceived = item.quantityReceived || null;
    // item.plannedReceiptDate = item.plannedReceiptDate || null;
    item.status = item.status || '';
    item.trackingNumber = item.trackingNumber || null;
    // item.estimatedDeliveryDate = item.estimatedDeliveryDate || null;
    item.createdBy = item.createdBy || this.USER.userId;
    item.createdByName = item.createdByName || this.USER.userName;
    item.lastUpdatedBy = item.lastUpdatedBy || this.USER.userId;
    item.lastUpdatedByName = item.lastUpdatedByName || this.USER.userName;
    item.isDeleted = item.isDeleted || false;
    return item;
  }

  getItemList(value: string = null) {
    // this.showLoader = true;
    this.commonService.getItems(value, 30, this.USER.company)
      .subscribe((res: ApiResponse) => {
        if (res && res.isSuccessful) {
          const existingItems = [];
          this.gridItems?.forEach(item => {
            if (item.itemCode !== this.itemsObject?.value && !item.isDeleted) {
              existingItems.push(item.itemCode);
            }
          });
          this.itemsList = res.result.filter(item => {
            item.value = item.item;
            item.text = `${item.item} | ${item.itemDescription}`;
            if (existingItems.length) {
              return !existingItems.includes(item.value)
            } else {
              return true;
            }
          });
          if (this.itemsObject) {
            this.itemsObject.dataSource = this.itemsList;
            this.itemsObject.hideSpinner();
          }
          this.showLoader = false;
        } else {
          this.itemsList = [];
          if (this.itemsObject) {
            this.itemsObject.hideSpinner();
            this.itemsObject.dataSource = [];
          }
          this.showLoader = false;
        }
      });
  }

  // ---- The grid ---------------------------------------------------------------------------------------------------
  // The rows are this.gridItems (the items input, LocalRowsGridApi, no endpoint). The columns stay getGridColumns and
  // every column's `edit` is drawn in its cell (CellEditorHost, @inavista/components controls), so the editors and
  // their rules run as before. Each grid row carries __row, its index in gridItems, so the handlers reach the page's own
  // item (rowData).

  setupGrid() {
    for (const c of this.columns as any[]) {
      if (c.edit) this.edits[c.field] = c.edit;
    }
    const { hosted, builtIn } = itemEditFields(this.columns);
    this.builtInEditFields = builtIn;
    const templates: Record<string, TemplateRef<unknown>> = {
      itemCode: this.itemCodeEditor, itemDescription: this.itemDescriptionEditor,
      serviceItemGroup: this.serviceItemGroupEditor, quantity: this.quantityEditor,
    };
    this.gridItems = this.items;
    this.gridConfig = buildItemsGridConfig({
      columns: this.columns,
      canEdit: this.status === partsOrderStatuses.open && !this.isManager && !this.isReadOnly,
      canAdd: !!this.commonService.roleClaims['PO_Popup_ItemTabGrid_Toolbar_Add_Item']?.visible,
      canExport: !!this.commonService.roleClaims['AllGrid_Toolbar_Excel_Export']?.visible,
      editors: Object.fromEntries(hosted.map(f => [f, templates[f]])),
      editLockReason: () => this.status === partsOrderStatuses.open ? null : 'Items can be changed while the order is Open.',
      addDisabledReason: () => this.status === partsOrderStatuses.open ? null : 'Items can be added while the order is Open.',
    });
    this.actionIcons = buildItemsActionIcons(() => this.deleteLockReason);
    // Fixed (Martin, 2026-10-28): the grid showed only once the old user-preference call (gridNames.partsOrderItemsGrid)
    // answered, never when it failed; that write-only preference (never applied: enablePersistence false) is replaced by
    // the grid's persistenceKey.
    this.showItemsGrid = true;
    this.refreshRows();
  }

  // The rows the grid shows: the items not deleted (the old grid's query isDeleted = false).
  refreshRows() {
    if (!this.gridConfig) return;
    const rows = (this.gridItems ?? [])
      .map((item, index) => ({ ...item, [ITEM_ROW_KEY]: index }))
      .filter(row => !row['isDeleted']);
    this.gridRows.set(PARTS_ORDER_ITEMS_URL, LocalRowsGridApi.schema(itemsSchema(this.columns)), rows);
    this.partsOrderItemsGrid?.refresh();
  }

  /** The page's item a grid row stands for. */
  itemFor(row: GridRow) {
    return row ? this.gridItems?.[itemRowKey(row)] : null;
  }

  // Leaving the open row saves it, as endEdit did.
  endEdit() {
    if (this.editing) {
      this.partsOrderItemsGrid?.saveBatch();
    }
  }

  private isColumnVisible(field: string): boolean {
    const visibility = this.partsOrderItemsGrid?.captureState()?.columnVisibility;
    if (visibility && field in visibility) return visibility[field];
    return (this.columns as any[]).find(c => c.field === field)?.visible !== false;
  }

  // A click on another row while one is open saves the open one, as the old mouseup handler's endEdit did.
  onRowClick(event: GridRowClickEvent) {
    if (this.editing && this.itemFor(event.row) !== this.hiddenEditRow) {
      this.endEdit();
    }
  }

  // The row opened: the old actionBegin('beginEdit'), then the editors draw.
  onRowEditStart(event: GridRowEditStartEvent) {
    const rowData = this.itemFor(event.row);
    if (!rowData) return;
    this.editing = true;
    this.actionBegin({ requestType: 'beginEdit', rowData });
    this.hiddenEditFields = itemFields(this.columns)
      .filter(c => this.edits[c.field] && !this.isColumnVisible(c.field))
      .map(c => c.field);
    this.hiddenEditRow = rowData;
    // Leaving the row always saves it, as the Syncfusion grid did: the row counts as changed from the start.
    const marked = this.partsOrderItemsGrid?.setDraftValue(ITEM_OPEN_FIELD, String(Date.now()));
    if (!marked) {
      setTimeout(() => this.partsOrderItemsGrid?.setDraftValue(ITEM_OPEN_FIELD, String(Date.now())));
    }
  }

  // Enter, the ✓ icon, a click elsewhere or validate(): every open editor's value goes into the item, as the
  // Syncfusion grid did, then the old actionComplete('save').
  onRowCommit(commit: GridRowCommitEvent) {
    const target = this.itemFor(commit.row);
    this.editing = false;
    this.hiddenEditRow = null;
    if (!target) return;
    for (const field of this.builtInEditFields) {
      if (!(field in commit.row)) continue;
      const value = commit.row[field];
      target[field] = NUMBER_FIELDS.has(field) && typeof value === 'string' ? (value === '' ? null : +value) : value;
    }
    for (const [field, edit] of this.openEditors) target[field] = edit.read();
    this.actionComplete({ requestType: 'save', rowData: target });
    this.refreshRows();
  }

  // Esc or ✕: the edits are dropped, as the old cancel.
  onRowRevert() {
    this.editing = false;
    this.hiddenEditRow = null;
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (this.deleteLockReason) return;
    const rowData = this.itemFor(event.row);
    if (rowData) this.commandColumnClick({ commandColumn: { title: event.iconKey }, rowData });
  }

  // Item and Description open the item; Shipment ID opens the shipment order, as before.
  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    const data = this.itemFor(event.row) ?? event.row;
    if (event.columnId === 'shipmentOrderLN') {
      this.onShipmentIdClick.emit({ orderId: data.shipmentOrderId, orderType: this.orderType });
    } else {
      this.showMasterItemCodePopup(data);
    }
  }

  onToolbarAction(event: GridToolbarActionClickEvent) {
    if (event.key === ADD_ITEM_ACTION) {
      this.toolbarClick('add-item');
    }
  }

  onExportRequested() {
    this.toolbarClick('grid_excelexport');
  }

  // Add Item cleared the filters, the search and the sort first.
  private clearGridView() {
    const grid = this.partsOrderItemsGrid;
    if (!grid) return;
    grid.applyState({ ...grid.captureState(), filters: {}, search: '', sort: null, sorts: [] });
  }

  actionBegin(args) {
    if (args && args.requestType === 'beginEdit') {
      if (args.rowData?.itemCode) {
        if (this.skipBeginEditEvent) {
          this.skipBeginEditEvent = false;
        } else {
          this.getItemList(args.rowData?.itemCode);
        }
      }
      else {
        this.getItemList();
      }
    }
  }

  actionComplete(args) {
    if (args.requestType === 'save') {
      this.toggleButtonDisable();
      const rowData = this.gridItems.find(item => item.lineNumber === args.rowData.lineNumber);
      rowData.itemGroup = this.itemGroup;
      // if(rowData.itemCode != ""){
      const emptyRecordsList = this.gridItems.filter((item, index) => {
        if (!item.itemCode) {
          item.rowIndex = index;
          return true;
        }
      });
      if (emptyRecordsList?.length < 2) {
        this.addItem();
        // Then the first empty row opens for editing, as the old grid did. Once the rows are back; not over a row the
        // user opened meanwhile (the old grid took them away from it).
        const empty = this.gridItems.findIndex(item => !item.itemCode && !item.isDeleted);
        if (empty >= 0) setTimeout(() => { if (!this.editing) this.partsOrderItemsGrid?.startEdit(empty); });
      }
      // }
    }
  }

  addItem() {
    let newIndex = this.gridItems.filter(item => !item.isDeleted).length;
    let newRow = this.setItemsRow({}, newIndex);
    this.gridItems.push(newRow);
    this.refreshRows();
  }

  getData() {
    return {
      itemList: this.gridItems?.filter(x => x.itemCode)
    }
  }

  commandColumnClick(args) {
    if (args.commandColumn.title === 'Delete Item') {
      this.endEdit();
      if (args.rowData.id || args.rowData.itemCode) {
        this.commonService.showConfirmation('Are you sure? You want to delete this item?').then(result => {
          if (result.isConfirmed) {
            const row = this.gridItems.find(x => x.lineNumber == args.rowData.lineNumber);
            row.lineNumber = args.rowData.lineNumber + '';
            row.isDeleted = true;
            this.resetLineNumbers();
            this.toggleButtonDisable();
          }
        })
      } else {
        const index = this.gridItems.findIndex(x => x.lineNumber === args.rowData.lineNumber);
        this.gridItems.splice(index, 1);
        this.resetLineNumbers();
        this.toggleButtonDisable();
      }
    }
  }

  toggleButtonDisable() {
    const elem: any = document.getElementById('parts-order-btn');
    if (!this.gridItems.filter(item => !item.isDeleted && item.itemCode).length) {
      // if (!this.items.filter(item => !item.isDeleted).length) {
      elem.disabled = true;
    } else {
      elem.disabled = this.status !== partsOrderStatuses.open;
    }
  }

  resetLineNumbers() {
    this.gridItems.map((item, index) => {
      item.lineNumber = index + 1;
      return item;
    });
    if (this.gridItems.filter(item => !item.itemCode).length < 2) {
      if (this.gridItems.filter(item => !item.itemCode).length === 0) {
        this.items.push(this.setItemsRow({}, this.items.length));
      }
      this.items.push(this.setItemsRow({}, this.items.length));
    }
    this.refreshRows();
  }

  getGridColumns() {
    // Delete Item is greyed (disabled-grid-button) unless the order is Open and the role may delete, as before.
    this.deleteLockReason = this.status !== partsOrderStatuses.open ? 'Items can be deleted while the order is Open.'
      : this.commonService.roleClaims['PO_Popup_ItemTabGrid_Action_Delete_Item']?.disable ? 'You do not have permission to delete items.'
      : null;
    let columns: any = [
      { field: 'lineNumber', headerText: 'No.', type: 'number', format: 'n', textAlign: 'Left', width: 100, isPrimaryKey: true, visible: false, showInColumnChooser: false },
      { field: 'itemCode', headerText: 'Item', type: 'string', textAlign: 'Left', filter: { operator: 'contains', type: 'CheckBox' }, showInColumnChooser: false, width: 250, edit: this.initItemsDDL(), editType: 'dropdownedit', validationRules: { required: false } },
      { field: 'itemDescription', headerText: 'Description', type: 'string', textAlign: 'Left', edit: this.initItemDescription(), showInColumnChooser: false },
      { field: 'serviceItemGroup', headerText: 'Service Item Group', type: 'string', textAlign: 'Left', filter: { type: 'Excel' }, width: 150, edit: this.initItemGroup() },
      { field: 'quantity', headerText: 'Ord Qty', type: 'number', format: 'n', step: 1, decimals: 0, validateDecimalOnType: true, textAlign: 'right', width: 142, showInColumnChooser: false, edit: this.initItemQuantity(), editType: 'numericedit', validationRules: { required: false } },
      { field: 'warehouseOrderLN', headerText: 'Order No.', type: 'string', textAlign: 'Left', width: 138, allowEditing: false, visible: false, },
      { field: 'lineNumberLN', headerText: 'Line No. (LN)', type: 'number', format: 'n', textAlign: 'Left', width: 158, allowEditing: false, visible: false },
      { field: 'sequenceLN', headerText: 'Sequence (LN)', type: 'number', format: 'n', textAlign: 'Left', width: 168, allowEditing: false, visible: false },
      { field: 'quantityOrderedLN', headerText: 'Ordered Qty (LN)', type: 'number', format: 'n', textAlign: 'Left', width: 179, allowEditing: false, visible: false },
      { field: 'status', headerText: 'Status', type: 'string', textAlign: 'Left', width: 120, allowEditing: false, visible: false, filter: { type: 'Excel' } },
      { field: 'estimatedDeliveryDate', headerText: 'Estimated Delivery Date', type: 'date', format: 'd MMM, y', textAlign: 'Left', width: 220, allowEditing: false, visible: false },
      { field: 'plannedReceiptDate', headerText: 'Planned Receipt Date', type: 'date', format: 'd MMM, y', textAlign: 'Left', width: 206, allowEditing: false, visible: false },
      { field: 'blockedLN', headerText: 'Blocked (LN)', type: 'string', textAlign: 'Left', width: 157, allowEditing: false, visible: false, filter: { type: 'Excel' } },
      { field: 'isDirectDelivery', headerText: 'Direct Delivery', type: 'string', textAlign: 'Left', width: 165, allowEditing: false, filter: { type: 'Excel' }, visible: false },
      { field: 'shipmentOrderLN', headerText: 'Shipment ID', type: 'string', textAlign: 'Left', width: 100, visible: false },
      { field: 'quantityShippedInInventoryUnit', headerText: 'Ship Qty', type: 'number', format: 'n', textAlign: 'right', width: 157, visible: false },
      { field: 'quantityReceived', headerText: 'Rcvd Qty', type: 'number', format: 'n', step: 1, decimals: 0, validateDecimalOnType: true, textAlign: 'right', width: 142, editType: 'numericedit', visible: false },
      { field: 'trackingNumber', headerText: 'Tracking Number', type: 'string', textAlign: 'Left', width: 182, allowEditing: false, visible: false },
      { field: 'lastUpdatedDate', headerText: 'Updated Date', type: 'date', format: 'd MMM, y', textAlign: 'Left', width: 191, allowEditing: false, visible: false },
      { field: 'lastUpdatedBy', headerText: 'Updated By', type: 'string', textAlign: 'Left', width: 149, allowEditing: false, visible: false, filter: { type: 'Excel' } },
      //{ field: 'lineNumberLN', headerText: 'Line No. (LN)', type: 'number', textAlign: 'Left', width: 250, allowEditing: false },
      //{ field: 'plannedReceiptDateLN', headerText: 'Planned Receipt Date', type: 'date', textAlign: 'Left', width: 250, allowEditing: false },
      { field: 'Actions', headerText: 'Actions', commands: [{ title: 'Delete Item' }] }
    ];
    return columns;
  }

  // The old toolbar's clicks: Excel export and Add Item (Columns, Search and Clear filters are the grid's own).
  toolbarClick(id: string) {
    if (this.editing) {
      this.commonService.showNotification('warning', 'Item is in edit mode!');
    }
    else {
      if (id === 'grid_excelexport') {
        exportInavistaGrid(this.partsOrderItemsGrid, 'Parts Order Items ', undefined, 'Parts Order Items');
      } else if (id === 'add-item') {
        this.showLoader = true;
        if (this.items[this.items.length - 1]?.itemCode == '' || this.items[this.items.length - 2]?.itemCode == '') {
          this.commonService.showNotification('warning', 'There are already empty row(s) present');
        }
        else {
          this.endEdit();
          if (!this.editing) {
            this.clearGridView();
            this.addItem();
          } else {
            this.commonService.showNotification('warning', 'Item is in edit mode!');
          }
        }
        this.showLoader = false;
      }
    }

  }

  validate() {
    this.validationApplied = true;
    this.endEdit();
    if (this.gridItems?.filter(x => !x.itemCode && x.itemDescription).length) {
      this.commonService.showNotification('error', 'Items grid validation failed. Item value is missing in a row no. ' + this.gridItems?.filter(x => !x.itemCode && x.itemDescription)[0].lineNumber);
      return false;
    } else if (this.gridItems?.filter(x => x.itemCode && !x.quantity).length) {
      this.commonService.showNotification('error', `There is an item with no or zero quantity ${this.gridItems?.filter(x => x.itemCode && !x.quantity)[0].lineNumber}`);
      return false;
    } else {
      if (Boolean(this.gridItems) && !this.editing) {
        return true;
      } else {
        return false;
      }
    }
  }

  removeItems() {
    this.gridItems = [];
    this.refreshRows();
  }

  showItemPopup() {
    this.showItemAdvancedSearchPopup = true;
  }

  onItemAdvancedSearchPopupClose(data: any) {
    setTimeout(() => {
      this.showItemAdvancedSearchPopup = false;
    }, 100);
    // const rowIndex = this.partsOrderItemsGrid.getRowIndexByPrimaryKey(this.selectedRowId);
    // this.partsOrderItemsGrid.selectRow(rowIndex);
    // setTimeout(() => {
    //   this.partsOrderItemsGrid.startEdit();
    //   this.skipBeginEditEvent = true;
    //   setTimeout(() => {
    this.itemsObject.value = data.item;
    this.itemsObject.dataSource = [{
      text: `${data.item} | ${data.itemDescription}`,
      value: data.item,
      ...data
    }];
    //   }, 400);
    // }, 300);
  }

  initItemsDDL(): CellEdit {
    return {
      read: () => this.itemsObject?.value,
      destroy: () => this.itemsObject?.destroy(),
      open: args => {
        this.selectedRowId = args.rowData.rowId;
        let currentItem;
        if (args.rowData.itemCode) {
          currentItem = {
            value: args.rowData.itemCode,
            text: `${args.rowData.itemCode} | ${args.rowData.itemDescription}`
          };
        }
        const tempDataSource = currentItem
            ? [currentItem, ...(this.itemsList || []).filter(item => item.value !== currentItem.value)]
            : this.itemsList;

        this.itemsObject = new DropdownEditor({
          dataSource: tempDataSource,
          fields: { value: 'value', text: 'text' },
          value: args.rowData.itemCode,
          allowFiltering: true,
          showClearButton: true,
          openOnFocus: true,
          enabled: this.status === partsOrderStatuses.open,
          filtering: args => { this.onItemUpdate.next(args.text); },
          action: { title: 'Advanced Search', iconClass: 'fas fa-info', click: () => this.showItemPopup() },
          change: (event: any) => {
            if (!event.value) { return; }
            if (event.itemData.status === PartsAvailabilityStatus.inActive) {
              if (event?.itemData?.alternateItem !== null) {
                this.commonService.showConfirmation('\nItem ' + event?.itemData?.item + '-' + event?.itemData?.itemDescription + ' is obsolete.' + '\n\n Would you like to add the alternate item ' + event?.itemData?.alternateItem)
                  .then(result => {
                    if (result.isConfirmed) {
                      // mapping data according to alternate item
                      if (this.gridItems.find(x => x.itemCode === event?.itemData?.alternateItem && !x.isDeleted)) {
                        this.commonService.showNotification('warning', "Item already added in the items list.");
                        this.itemsObject.value = null;
                        return;
                      } else {
                        this.itemsObject.value = null;
                        this.commonService.getAlternateItemData(event?.itemData?.alternateItem)
                          .subscribe((res: ApiResponse) => {
                            this.itemsObject.dataSource = [{
                              text: `${res.result.item} | ${res.result.itemDescription}`,
                              value: res.result.item,
                              ...res.result
                            }];
                            this.itemsObject.value = res.result.item;
                            this.itemGroup = res.result.itemGroup;
                            this.itemGroupObject.value = res.result.serviceItemGroup;
                            this.itemDescriptionObject.value = res.result.itemDescription || res.result.description;
                          })
                      }
                    } else {
                      this.itemsObject.value = null;
                      return;
                    }
                  })
              } else {
                this.commonService.showNotification('error', '\nItem ' + event?.itemData?.item + '-' + event?.itemData?.itemDescription + ' is obsolete');
                this.itemsObject.value = null;
                return;
              }
            }
            else {
              this.itemGroup = event.itemData.itemGroup;
              this.itemGroupObject.value = event.itemData.serviceItemGroup;
              this.itemDescriptionObject.value = event.itemData.description || event.itemData.itemDescription;
              if (event.isInteracted) {
                // this.itemQuantityObject.value = 1;
                this.itemQuantityObject.max = this.inventoryItems?.find(x => x.itemCode === event.value)?.onHand;
              }
            }

          }
        });
        // The Advanced Search button is always shown on this grid.
        this.itemsObject.actionVisible.set(true);
        return this.itemsObject;
      }
    }
  }

  initItemGroup(): CellEdit {
    return {
      read: () => this.itemGroupObject?.value,
      destroy: () => this.itemGroupObject?.destroy(),
      open: args => {
        return this.itemGroupObject = new TextEditor({
          value: args.rowData.serviceItemGroup,
          enabled: false
        });
      }
    }
  }

  initItemDescription(): CellEdit {
    return {
      read: () => this.itemDescriptionObject?.value,
      destroy: () => this.itemDescriptionObject?.destroy(),
      open: args => {
        return this.itemDescriptionObject = new TextEditor({
          value: args.rowData.itemDescription,
          enabled: false
        });
      }
    }
  }

  initItemQuantity(): CellEdit {
    return {
      read: () => this.itemQuantityObject?.value,
      destroy: () => this.itemQuantityObject?.destroy(),
      open: args => {
        return this.itemQuantityObject = new NumberEditor({
          value: args.rowData.quantity || 1,
          min: 1,
          step: 1,
          decimals: 0,
        });
      }
    }
  }

  ngOnDestroy() {
    this.status = '';
    this.items.length = 0;
    this.inventoryItems = [];
    this.reasonsDDL = [];
    this.onItemUpdate.unsubscribe();
  }

}
